import { gql, useMutation, useQuery } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';

import { Code } from 'src/components/code/Code';
import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';
import {
  GRAPH_VARIANT_PLACEHOLDER,
  introspectionToGraphPublish,
  schemaFileToGraphPublish,
} from 'src/lib/roverCommands';

export const RoverAddVariantModal = ({
  isOpen,
  onClose,
  graphId,
  title,
  description,
}: {
  isOpen: boolean;
  onClose: () => void;
  graphId: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
}) => {
  const [schemaPublishSchemaSource, setSchemaPublishSchemaSource] = useState<
    'schemaFile' | 'introspection'
  >('schemaFile');

  const { data: userData } = useQuery<
    GraphQLTypes.AddVariantModalQuery,
    GraphQLTypes.AddVariantModalQueryVariables
  >(gql`
    query AddVariantModalQuery {
      me {
        id
      }
    }
  `);

  const [provisionKey, { data, error }] = useMutation<
    GraphQLTypes.AddVariantModalProvisionKeyMutation,
    GraphQLTypes.AddVariantModalProvisionKeyMutationVariables
  >(gql`
    mutation AddVariantModalProvisionKeyMutation($userId: ID!) {
      user(id: $userId) {
        provisionKey {
          apiKey {
            id
            token
          }
        }
      }
    }
  `);

  React.useEffect(() => {
    if (userData?.me?.id) {
      provisionKey({
        variables: {
          userId: userData.me.id,
        },
      });
    }
  }, [provisionKey, userData]);

  const key = data?.user?.provisionKey?.apiKey.token;

  const graphPublish =
    schemaPublishSchemaSource === 'schemaFile'
      ? schemaFileToGraphPublish({
          graphId,
          graphVariant: GRAPH_VARIANT_PLACEHOLDER,
          highlightGraphRef: true,
        })
      : introspectionToGraphPublish({
          graphId,
          graphVariant: GRAPH_VARIANT_PLACEHOLDER,
          highlightGraphRef: true,
        });

  const initialRef = React.useRef(null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
      initialFocusRef={initialRef}
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          <ModalDescription>{description}</ModalDescription>
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p>
              1. Let’s start by installing Rover
              <br />
              <span className="italic">
                If you already have Rover installed, skip to the next step!
              </span>
            </p>

            <Code
              title="Linux / MacOS installer"
              firstLineDecorator="$ "
              code="curl -sSL https://rover.apollo.dev/nix/latest | sh"
              showCopyButton
            />

            <Code
              title="Windows PowerShell"
              firstLineDecorator="> "
              code="iwr 'https://rover.apollo.dev/win/latest' | iex"
              showCopyButton
            />
          </div>
          <div className="mt-4 flex flex-col gap-4">
            <p className="text-base">2. Connect your CLI to Studio:</p>
            <Code
              firstLineDecorator="$ "
              code="rover config auth"
              showCopyButton
            />
            <div className="flex items-center">
              <IconInfo className="mr-2 text-secondary" />
              <Link
                isExternal
                to={DocsRouter.path('RoverCiCdSetup')}
                className="text-sm"
              >
                Learn how to use Rover to setup your CI/CD.
              </Link>
              <IconOutlink className="ml-1 text-link" />
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-4">
            <p className="text-base">
              3. Copy this user token we’ve automatically generated for you and
              provide it to Rover.
            </p>
            <Code
              code={key ?? '[Token Unavailable]'}
              mask={error || !key ? undefined : key.split(':').pop()}
              showCopyButton
            />
          </div>
          <div className="mt-4 flex flex-col gap-4">
            <p className="text-base">
              4. Finally, publish your new variant by running the following
              command and replacing{' '}
              <span className="font-semibold">your-variant-name</span> with your
              new variant name:
            </p>
            <div className="flex gap-4 border-b border-primary">
              <HeaderTab
                className="focus-visible:focus-ring mx-0"
                tabID="schemaFile"
                tabName="Schema File"
                isActive={() => schemaPublishSchemaSource === 'schemaFile'}
                onClick={() => {
                  setSchemaPublishSchemaSource('schemaFile');
                }}
              />
              <HeaderTab
                className="focus-visible:focus-ring mx-0"
                tabID="introspection"
                tabName="Introspection"
                isActive={() => schemaPublishSchemaSource === 'introspection'}
                onClick={() => {
                  setSchemaPublishSchemaSource('introspection');
                }}
              />
            </div>
            <Code
              firstLineDecorator="$ "
              code={graphPublish.shellString}
              showCopyButton
              highlight={graphPublish.shellStringLinesToHighlight}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} ref={initialRef}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
